import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobileDevice, isLocalDevelopment } from '../../utils';
import './OtpStep.css';
const OtpStep = ({ sessionId, loading, error, onVerifyOtp, onNext, onResendOtp, }) => {
    const { t } = useTranslation();
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [verified, setVerified] = useState(false);
    const [waitingForSms, setWaitingForSms] = useState(false);
    const [hasSubmittedOtp, setHasSubmittedOtp] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(600);
    const inputRefs = useRef([]);
    useEffect(() => {
        const otpString = otp.join('');
        if (otpString.length === 6 && !loading && !verified && !hasSubmittedOtp && !error) {
            if (isLocalDevelopment()) {
                console.log('[OtpStep] OTP complete, auto-submitting...');
            }
            (async () => {
                setHasSubmittedOtp(true);
                setVerified(false);
                try {
                    const success = await onVerifyOtp(sessionId, otpString);
                    if (success) {
                        setVerified(true);
                        setTimeout(() => {
                            onNext();
                        }, 2000);
                    }
                    else {
                        if (isLocalDevelopment()) {
                            console.log('[OtpStep] OTP verification failed, preventing re-submission');
                        }
                    }
                }
                catch (err) {
                    console.error('Error verifying OTP:', err);
                }
            })();
        }
    }, [otp, loading, verified, hasSubmittedOtp, error, sessionId, onVerifyOtp, onNext]);
    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
        const isMobile = isMobileDevice();
        if ('OTPCredential' in window && isMobile) {
            const abortController = new AbortController();
            const requestOtp = async () => {
                try {
                    if (isLocalDevelopment()) {
                        console.log('[OtpStep] Requesting OTP via WebOTP API on mobile device...');
                    }
                    setWaitingForSms(true);
                    const credential = await navigator.credentials.get({
                        otp: { transport: ['sms'] },
                        signal: abortController.signal
                    });
                    if (credential && credential.code) {
                        if (isLocalDevelopment()) {
                            console.log('[OtpStep] OTP received via WebOTP:', credential.code);
                        }
                        const otpArray = credential.code.split('').slice(0, 6);
                        setOtp(prev => {
                            const newOtp = [...prev];
                            otpArray.forEach((digit, index) => {
                                if (index < 6) {
                                    newOtp[index] = digit;
                                }
                            });
                            return newOtp;
                        });
                        if (inputRefs.current[5]) {
                            inputRefs.current[5].focus();
                        }
                        setWaitingForSms(false);
                    }
                }
                catch (err) {
                    if (isLocalDevelopment()) {
                        console.log('[OtpStep] WebOTP cancelled or not available:', err);
                    }
                    setWaitingForSms(false);
                }
            };
            requestOtp();
            return () => {
                abortController.abort();
            };
        }
        else {
            if (isLocalDevelopment()) {
                if (!isMobile) {
                    console.log('[OtpStep] WebOTP API disabled on desktop device');
                }
                else {
                    console.log('[OtpStep] WebOTP API not available on this device/browser');
                }
            }
        }
    }, []);
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);
    useEffect(() => {
        if (resendSuccess) {
            const timer = setTimeout(() => {
                setResendSuccess(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [resendSuccess]);
    useEffect(() => {
        if (timeRemaining > 0 && !verified) {
            const timer = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [timeRemaining, verified]);
    useEffect(() => {
        if (resendSuccess) {
            setTimeRemaining(600);
        }
    }, [resendSuccess]);
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };
    const handleInputChange = (index, value) => {
        if (hasSubmittedOtp) {
            setHasSubmittedOtp(false);
        }
        if (value.length > 1) {
            const pastedOtp = value.slice(0, 6).split('');
            const newOtp = [...otp];
            pastedOtp.forEach((digit, i) => {
                if (index + i < 6) {
                    newOtp[index + i] = digit;
                }
            });
            setOtp(newOtp);
            const nextIndex = Math.min(index + pastedOtp.length, 5);
            if (inputRefs.current[nextIndex]) {
                inputRefs.current[nextIndex]?.focus();
            }
        }
        else {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            if (value && index < 5 && inputRefs.current[index + 1]) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (hasSubmittedOtp) {
                setHasSubmittedOtp(false);
            }
            if (!otp[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        }
    };
    const handleClear = () => {
        setOtp(['', '', '', '', '', '']);
        setHasSubmittedOtp(false);
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    };
    const handleResend = async (medium) => {
        if (!onResendOtp || resendLoading || resendCooldown > 0)
            return;
        setResendLoading(true);
        setResendSuccess(false);
        setOtp(['', '', '', '', '', '']);
        setHasSubmittedOtp(false);
        try {
            const success = await onResendOtp(medium);
            if (success) {
                setResendSuccess(true);
                setResendCooldown(30);
                if (inputRefs.current[0]) {
                    inputRefs.current[0].focus();
                }
            }
        }
        catch (err) {
            console.error('Error resending OTP:', err);
        }
        finally {
            setResendLoading(false);
        }
    };
    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        const otpString = otp.join('');
        if (otpString.length !== 6 || loading) {
            return;
        }
        setHasSubmittedOtp(true);
        setVerified(false);
        try {
            const success = await onVerifyOtp(sessionId, otpString);
            if (success) {
                setVerified(true);
                setTimeout(() => {
                    onNext();
                }, 2000);
            }
            else {
                if (isLocalDevelopment()) {
                    console.log('[OtpStep] Manual OTP verification failed');
                }
            }
        }
        catch (err) {
            console.error('Error verifying OTP:', err);
        }
    };
    if (verified && !error) {
        return (_jsx("div", { className: "otp-step", children: _jsxs("div", { className: "card", children: [_jsx("div", { className: "card-header", children: _jsx("h2", { children: t('otp.title') }) }), _jsx("div", { className: "card-body", children: _jsx("div", { className: "success-container", children: _jsxs("div", { className: "alert alert-success", children: [_jsx("div", { className: "success-icon", children: "\u2705" }), _jsx("p", { children: "Verification successful!" }), _jsx("p", { children: "Loading your card details..." })] }) }) })] }) }));
    }
    return (_jsx("div", { className: "otp-step", children: _jsxs("div", { className: "card otp-card", children: [_jsxs("div", { className: "card-header otp-header", children: [_jsx("h2", { children: t('otp.title') }), _jsx("p", { children: t('otp.description') }), _jsx("p", { className: "delivery-time-message", children: t('otp.deliveryTime') })] }), _jsx("div", { className: "card-body otp-body", children: _jsxs("form", { onSubmit: handleSubmit, className: "otp-form", children: [waitingForSms && (_jsx("div", { className: "sms-waiting-indicator", children: _jsxs("div", { className: "info-box", children: [_jsx("span", { className: "spinner-small" }), _jsx("span", { children: "Waiting for SMS... Your code will be filled automatically" })] }) })), _jsx("div", { className: "otp-inputs", children: otp.map((digit, index) => (_jsx("input", { ref: (el) => (inputRefs.current[index] = el), type: "text", inputMode: "numeric", pattern: "[0-9]*", maxLength: 6, value: digit, onChange: (e) => handleInputChange(index, e.target.value.replace(/\D/g, '')), onKeyDown: (e) => handleKeyDown(index, e), className: "otp-input", disabled: loading, placeholder: "", autoComplete: index === 0 ? "one-time-code" : "off" }, index))) }), otp.some(digit => digit !== '') && !loading && (_jsx("div", { className: "clear-otp-container", children: _jsxs("button", { type: "button", className: "btn-link clear-otp-btn", onClick: handleClear, title: t('otp.clearOtpTitle'), children: [_jsx("i", { className: "fa-solid fa-times-circle" }), " ", t('otp.clear')] }) })), !loading && !error && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "auto-verify-hint", children: [_jsx("i", { className: "fa-solid fa-circle-info" }), _jsx("span", { children: t('otp.autoVerifyHint') })] }), timeRemaining > 0 ? (_jsxs("div", { className: "countdown-timer", children: [_jsx("i", { className: "fa-regular fa-clock" }), _jsx("span", { children: t('otp.expiresIn', { time: formatTime(timeRemaining) }) })] })) : (_jsxs("div", { className: "countdown-timer expired", children: [_jsx("i", { className: "fa-solid fa-exclamation-triangle" }), _jsx("span", { children: t('otp.expired') })] }))] })), error && (_jsxs("div", { className: "alert alert-error", children: [_jsx("strong", { children: t('common.error') }), _jsx("p", { children: error })] })), loading && (_jsxs("div", { className: "verification-status", children: [_jsx("span", { className: "btn-spinner" }), _jsx("span", { children: t('otp.verifying') })] })), error && (_jsx("div", { className: "form-actions", children: _jsx("button", { type: "submit", className: "btn btn-primary btn-large", disabled: otp.join('').length !== 6, children: t('otp.retry') }) })), _jsxs("div", { className: "resend-container", children: [resendSuccess && (_jsxs("div", { className: "resend-success", children: [_jsx("span", { className: "success-icon", children: "\u2713" }), t('otp.resendSuccess')] })), _jsxs("div", { className: "resend-options", children: [_jsx("span", { className: "resend-label", children: t('otp.resendLabel') }), _jsxs("div", { className: "resend-buttons", children: [_jsx("button", { type: "button", className: "btn-link resend-link", onClick: () => handleResend('sms'), disabled: !onResendOtp || loading || resendLoading || resendCooldown > 0, children: resendLoading ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "btn-spinner-small" }), t('otp.resending')] })) : resendCooldown > 0 ? (`${t('otp.resendSms')} (${resendCooldown}s)`) : (_jsxs(_Fragment, { children: [_jsx("i", { className: "fa-solid fa-message" }), t('otp.resendSms')] })) }), _jsx("span", { className: "resend-separator", children: "|" }), _jsx("button", { type: "button", className: "btn-link resend-link", onClick: () => handleResend('voice'), disabled: !onResendOtp || loading || resendLoading || resendCooldown > 0, children: resendLoading ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "btn-spinner-small" }), t('otp.resending')] })) : resendCooldown > 0 ? (`${t('otp.resendVoice')} (${resendCooldown}s)`) : (_jsxs(_Fragment, { children: [_jsx("i", { className: "fa-solid fa-phone" }), t('otp.resendVoice')] })) })] })] })] })] }) })] }) }));
};
export default OtpStep;
